import { makeStyles, Theme, alpha } from '@material-ui/core/styles';

export const INFO_600 = 'rgb(46, 98, 255)';
export const NEUTRAL_50 = 'rgb(247, 248, 252)';
export const NEUTRAL_700 = 'rgb(94, 105, 166)';
export const RC_INK = 'rgb(0, 0, 7)';
export const MUTED_INFO_TEXT = '#5B6B99';
export const SUCCESS_GREEN = 'rgb(6, 148, 108)';
export const FONT_DISPLAY = '"Figtree", system-ui, sans-serif';

export const useTrackRecordV2Styles = makeStyles((theme: Theme) => ({
  root: {
    '--info-600': INFO_600,
    '--neutral-50': NEUTRAL_50,
    '--neutral-700': NEUTRAL_700,
    '--muted-foreground': 'var(--neutral-700)',
    '--rc-ink': RC_INK,
    '--rc-bg': 'var(--neutral-50)',
    '--rc-border': alpha(INFO_600, 0.3),
    '--font-display': FONT_DISPLAY,
    width: '100%',
    maxWidth: 1600,
    height: '90vh',
    // The modal itself doesn't scroll — header/tabs/footer stay put and only
    // `scrollArea` (the tab body) scrolls. See below.
    display: 'flex',
    flexDirection: 'column',
    margin: '0 auto',
    backgroundColor: theme.palette.background.paper,
    borderRadius: 24,
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(4, 5, 3),
    boxSizing: 'border-box',
    fontFamily: 'var(--font-display)',
    // Force every Typography/form control back to Figtree (MUI sets its own inline).
    '& .MuiTypography-root, & input, & button': {
      fontFamily: 'inherit',
    },
  },
  // Only this area scrolls — everything else (header, tabs, footer) is static.
  scrollArea: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
  },
  headerRow: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(3),
  },
  headerTitle: {
    fontWeight: 700,
  },
  tabsRow: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(4),
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(4),
  },
  tab: {
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: 0.3,
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    padding: theme.spacing(1.5, 0),
    borderBottom: '2px solid transparent',
  },
  tabActive: {
    color: 'var(--info-600)',
    borderBottom: '2px solid var(--info-600)',
  },
  title: {
    fontWeight: 700,
    marginBottom: theme.spacing(1),
  },
  subtitle: {
    fontSize: 13,
    color: 'var(--muted-foreground)',
    maxWidth: 720,
    marginBottom: theme.spacing(3),
  },
  card: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    padding: theme.spacing(3),
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2.5),
  },
  cardHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  cardHeaderHint: {
    fontSize: 13,
    color: 'var(--info-600)',
  },
  cardHeaderTitle: {
    fontWeight: 700,
  },
  stepBadge: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 13,
    fontWeight: 700,
    flexShrink: 0,
  },
  // .rc-badge + .rc-badge--outline from the real design system.
  stepChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    height: 20,
    padding: '0 10px',
    borderRadius: 9999,
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    backgroundColor: 'var(--rc-bg)',
    color: 'var(--rc-ink)',
    border: '1px solid var(--rc-border)',
  },
  questionCard: {
    backgroundColor: 'var(--neutral-50)',
    borderRadius: 8,
    padding: theme.spacing(2, 2.5),
    marginBottom: theme.spacing(1.5),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  questionCardNoMargin: {
    backgroundColor: 'var(--neutral-50)',
    borderRadius: 8,
    padding: theme.spacing(2, 2.5),
  },
  questionRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
  },
  questionLabel: {
    fontSize: 15,
  },
  uploadArea: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5),
    border: `1px dashed ${alpha(INFO_600, 0.4)}`,
    borderRadius: 8,
    padding: theme.spacing(1.5, 2),
    backgroundColor: alpha(INFO_600, 0.04),
    cursor: 'pointer',
  },
  uploadIcon: {
    color: 'var(--info-600)',
    fontSize: 20,
    flexShrink: 0,
  },
  uploadText: {
    fontSize: 14,
    color: theme.palette.text.primary,
  },
  uploadLink: {
    color: 'var(--info-600)',
    fontWeight: 600,
  },
  // Yes/No is a single switch, not two separate buttons — reuses the same
  // track/pill look as the STATE and VIEW AS toggles in the header.
  answerGroup: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    flexShrink: 0,
    backgroundColor: 'white',
    borderRadius: 999,
    padding: 4,
  },
  answerButton: {
    minWidth: 56,
    border: 'none',
    outline: 'none',
    borderRadius: 999,
    backgroundColor: 'transparent',
    color: 'black',
    fontSize: 13,
    fontWeight: 600,
    padding: theme.spacing(0.75, 2),
    cursor: 'pointer',
    transition: 'background-color 0.15s, color 0.15s',
  },
  // Selected answer always fills black, in both Borrower and Underwriter views.
  answerButtonSelected: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  footer: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderTop: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(3),
    paddingTop: theme.spacing(2.5),
  },
  footerHelperText: {
    color: theme.palette.text.secondary,
    fontSize: 13,
  },
  saveButton: {
    border: 'none',
    borderRadius: 8,
    padding: theme.spacing(1.25, 3),
    fontSize: 14,
    fontWeight: 700,
    color: theme.palette.common.white,
    backgroundColor: theme.palette.common.black,
    cursor: 'pointer',
    '&:disabled': {
      backgroundColor: theme.palette.action.disabledBackground,
      color: theme.palette.text.disabled,
      cursor: 'not-allowed',
    },
  },
  chatPlaceholder: {
    color: theme.palette.text.secondary,
    padding: theme.spacing(6, 0),
    textAlign: 'center',
  },
  chatContainer: {
    height: '65vh',
    border: '1px solid #CBCBCB',
    borderRadius: 10,
    padding: theme.spacing(1),
  },
  chatEmptyContainer: {
    height: '65vh',
    border: '1px solid #CBCBCB',
    borderRadius: 10,
    padding: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
  },
  chatStartButton: {
    letterSpacing: 'normal',
    textTransform: 'none',
    fontWeight: 'normal',
    fontSize: 16,
    backgroundColor: '#4285F4',
    width: 307,
    height: 64,
    padding: theme.spacing(1, 2),
    boxShadow: '0px 4px 4px 0px #00000040',
    '&:hover': {
      backgroundColor: '#357AE8',
    },
  },
  chatIconCircle: {
    width: 44,
    height: 44,
    borderRadius: '50%',
    backgroundColor: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing(0.5),
  },
  methodOptionsRow: {
    display: 'flex',
    gap: theme.spacing(2),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  agentSearchBanner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    backgroundColor: alpha(INFO_600, 0.08),
    border: `1px solid ${alpha(INFO_600, 0.24)}`,
    borderRadius: 12,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  agentSearchBannerTitle: {
    fontWeight: 700,
    fontSize: 14,
    color: theme.palette.text.primary,
  },
  agentSearchBannerSubtext: {
    fontSize: 12,
    color: MUTED_INFO_TEXT,
    marginTop: theme.spacing(0.5),
  },
  queuedSearchRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
    backgroundColor: alpha(INFO_600, 0.08),
    border: `1px solid ${alpha(INFO_600, 0.24)}`,
    borderRadius: 12,
    padding: theme.spacing(1.5, 2),
    marginTop: theme.spacing(1.5),
  },
  queuedSearchRowLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  queuedSearchLabel: {
    fontWeight: 700,
    fontSize: 14,
    color: theme.palette.text.primary,
  },
  queuedSearchSubtext: {
    fontSize: 12,
    color: MUTED_INFO_TEXT,
  },
  queuedSearchTimer: {
    fontWeight: 600,
    fontSize: 13,
    color: 'var(--info-600)',
    flexShrink: 0,
  },
  queuedSearchRowDone: {
    backgroundColor: alpha(SUCCESS_GREEN, 0.1),
    border: `1px solid ${alpha(SUCCESS_GREEN, 0.28)}`,
  },
  queuedSearchDoneTimer: {
    fontWeight: 600,
    fontSize: 13,
    color: SUCCESS_GREEN,
    flexShrink: 0,
  },
  agentSearchBannerDone: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    backgroundColor: alpha(SUCCESS_GREEN, 0.1),
    border: `1px solid ${alpha(SUCCESS_GREEN, 0.28)}`,
    borderRadius: 999,
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(2),
  },
  agentSearchBannerDoneTitle: {
    flex: 1,
    fontWeight: 700,
    fontSize: 14,
    color: theme.palette.text.primary,
  },
  agentSearchBannerDoneActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexShrink: 0,
  },
  bannerButtonOutline: {
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    borderRadius: 999,
    padding: theme.spacing(0.75, 2),
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    color: theme.palette.text.primary,
  },
  bannerButtonSolid: {
    border: 'none',
    backgroundColor: RC_INK,
    color: theme.palette.background.paper,
    borderRadius: 999,
    padding: theme.spacing(0.75, 2),
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
  },
  methodCard: {
    position: 'relative',
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(2),
    cursor: 'pointer',
    backgroundColor: theme.palette.background.paper,
    transition: 'background-color 0.15s, border-color 0.15s',
  },
  methodCardSelected: {
    border: '1px solid var(--info-600)',
    backgroundColor: alpha(INFO_600, 0.06),
  },
  recommendedChip: {
    position: 'absolute',
    top: -10,
    left: 16,
    fontSize: 11,
    fontWeight: 700,
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.grey[300],
    borderRadius: 999,
    padding: theme.spacing(0.3, 1.25),
  },
  methodIcon: {
    color: 'var(--info-600)',
    fontSize: 22,
    flexShrink: 0,
  },
  methodTitle: {
    fontWeight: 700,
    fontSize: 14,
  },
  methodSubtitle: {
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
  dropZone: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    gap: theme.spacing(0.5),
    border: `1px dashed ${alpha(INFO_600, 0.4)}`,
    borderRadius: 8,
    padding: theme.spacing(5, 2),
    backgroundColor: alpha(INFO_600, 0.04),
    cursor: 'pointer',
    outline: 'none',
  },
  dropZoneIcon: {
    color: 'var(--info-600)',
    fontSize: 32,
    marginBottom: theme.spacing(1),
  },
  dropZoneText: {
    fontSize: 15,
  },
  dropZoneLink: {
    color: 'var(--info-600)',
    fontWeight: 600,
    cursor: 'pointer',
  },
  dropZoneSubtext: {
    fontSize: 13,
    color: theme.palette.text.secondary,
  },
  searchHelperText: {
    fontSize: 14,
    color: 'var(--info-600)',
    marginBottom: theme.spacing(1.5),
  },
  searchRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  searchRowSpaced: {
    marginBottom: theme.spacing(1.5),
  },
  searchInput: {
    flex: 1,
    border: `1px solid ${alpha(INFO_600, 0.3)}`,
    borderRadius: 999,
    padding: theme.spacing(1.25, 2.5),
    fontSize: 14,
    outline: 'none',
    fontFamily: 'inherit',
    '&::placeholder': {
      color: theme.palette.text.disabled,
    },
  },
  searchInputAddress: {
    flex: 2.6,
  },
  searchInputApt: {
    flex: 1,
  },
  searchInputCity: {
    flex: 1.2,
  },
  searchInputState: {
    flex: 1,
  },
  searchInputZip: {
    flex: 1,
  },
  searchButton: {
    border: '1px solid var(--info-600)',
    borderRadius: 999,
    backgroundColor: 'transparent',
    color: 'var(--info-600)',
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: 0.3,
    textTransform: 'uppercase',
    padding: theme.spacing(1.25, 3),
    cursor: 'pointer',
    flexShrink: 0,
  },
  // ── "Has records" Overview dashboard ──────────────────────────────────
  dashboardContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(3),
  },
  sidebar: {
    width: 260,
    flexShrink: 0,
    backgroundColor: alpha(INFO_600, 0.05),
    borderRadius: 12,
    padding: theme.spacing(2.5),
    boxSizing: 'border-box',
  },
  sidebarLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    color: MUTED_INFO_TEXT,
  },
  sidebarBigNumberRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(1),
    margin: theme.spacing(0.5, 0, 1.5),
  },
  sidebarBigNumber: {
    fontSize: 40,
    fontWeight: 800,
    lineHeight: 1,
  },
  sidebarBigNumberLabel: {
    fontSize: 15,
    color: theme.palette.text.secondary,
  },
  progressTrack: {
    height: 6,
    borderRadius: 999,
    backgroundColor: alpha(INFO_600, 0.15),
    overflow: 'hidden',
    marginBottom: theme.spacing(1.5),
  },
  progressFill: {
    height: '100%',
    backgroundColor: theme.palette.success.main,
    borderRadius: 999,
  },
  sidebarDivider: {
    height: 1,
    backgroundColor: theme.palette.divider,
    margin: theme.spacing(0, 0, 1.5),
  },
  navList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(3),
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderRadius: 8,
    padding: theme.spacing(1, 1.5),
    cursor: 'pointer',
    fontSize: 14,
    color: theme.palette.text.primary,
  },
  navItemActive: {
    backgroundColor: theme.palette.background.paper,
    fontWeight: 700,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
  navBadges: {
    display: 'flex',
    gap: theme.spacing(0.5),
  },
  navBadgeGood: {
    minWidth: 20,
    textAlign: 'center',
    fontSize: 11,
    fontWeight: 700,
    borderRadius: 999,
    padding: '1px 6px',
    color: theme.palette.success.dark,
    backgroundColor: alpha(theme.palette.success.main, 0.15),
  },
  navBadgeBad: {
    minWidth: 20,
    textAlign: 'center',
    fontSize: 11,
    fontWeight: 700,
    borderRadius: 999,
    padding: '1px 6px',
    color: theme.palette.error.dark,
    backgroundColor: alpha(theme.palette.error.main, 0.15),
  },
  templateCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(2),
  },
  templateCardTitle: {
    fontWeight: 700,
    fontSize: 14,
    marginBottom: theme.spacing(0.5),
  },
  templateCardText: {
    fontSize: 13,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  templateCardLink: {
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--info-600)',
    cursor: 'pointer',
  },
  mainContent: {
    flex: 1,
    minWidth: 0,
    padding: theme.spacing(2),
  },
  overviewTitle: {
    fontWeight: 700,
  },
  overviewSubtitle: {
    color: MUTED_INFO_TEXT,
    marginBottom: theme.spacing(3),
  },
  summaryRow: {
    display: 'flex',
    // baseline (not flex-start) so the 26px numbers and 15px titles align by text, not box top.
    alignItems: 'baseline',
    // space-between divides the width equally across all 5 columns.
    justifyContent: 'space-between',
    gap: theme.spacing(5),
  },
  summaryVerifiedBadgeCell: {
    alignSelf: 'center',
  },
  summaryStatTitle: {
    fontWeight: 700,
    fontSize: 15,
  },
  summaryStatBigRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(0.75),
  },
  summaryStatBig: {
    fontSize: 26,
    fontWeight: 800,
  },
  summaryStatBigLabel: {
    fontSize: 15,
    fontWeight: 700,
  },
  summaryStatCaption: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.3,
    textTransform: 'uppercase',
    color: MUTED_INFO_TEXT,
    marginTop: theme.spacing(0.5),
  },
  verifiedBadge: {
    fontSize: 12,
    fontWeight: 700,
    color: theme.palette.success.dark,
    backgroundColor: alpha(theme.palette.success.main, 0.15),
    borderRadius: 999,
    padding: theme.spacing(0.4, 1.5),
  },
  experienceRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(4),
    marginBottom: theme.spacing(2.5),
  },
  experienceLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.3,
    textTransform: 'uppercase',
    color: MUTED_INFO_TEXT,
    marginBottom: theme.spacing(0.5),
  },
  experienceValue: {
    fontSize: 15,
    fontWeight: 700,
  },
  editLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0.3,
    textTransform: 'uppercase',
    color: 'var(--info-600)',
    cursor: 'pointer',
  },
  eligibilityGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(2),
  },
  eligibilityGridFullRow: {
    gridColumn: '1 / -1',
  },
  skipButton: {
    border: 'none',
    borderRadius: 8,
    padding: theme.spacing(1.25, 3),
    fontSize: 14,
    fontWeight: 700,
    color: theme.palette.text.primary,
    backgroundColor: alpha(INFO_600, 0.1),
    cursor: 'pointer',
  },
  acceptButton: {
    border: 'none',
    borderRadius: 8,
    padding: theme.spacing(1.25, 3),
    fontSize: 14,
    fontWeight: 700,
    color: theme.palette.common.white,
    backgroundColor: theme.palette.common.black,
    cursor: 'pointer',
  },
  dashboardActions: {
    display: 'flex',
    gap: theme.spacing(1.5),
  },
  // ── Properties list ────────────────────────────────────────────────────
  listHeaderRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2.5),
  },
  filterRow: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2.5),
  },
  filterChip: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 999,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    fontSize: 13,
    fontWeight: 600,
    padding: theme.spacing(0.75, 2),
    cursor: 'pointer',
  },
  filterChipActive: {
    border: `1px solid ${theme.palette.common.black}`,
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  table: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    overflow: 'hidden',
  },
  tableRow: {
    display: 'grid',
    gridTemplateColumns: '2fr 1.6fr 1.6fr 1fr 1fr 1.1fr 32px 32px',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1.75, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  tableRowContainer: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  tableHeaderRow: {
    backgroundColor: theme.palette.grey[50],
  },
  tableHeaderCell: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.3,
    textTransform: 'uppercase',
    color: MUTED_INFO_TEXT,
  },
  tableRowNeedsInput: {
    backgroundColor: alpha(theme.palette.error.main, 0.05),
  },
  tableCellPrimary: {
    fontSize: 14,
    fontWeight: 700,
  },
  tableCellSecondary: {
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
  tableCell: {
    fontSize: 13,
  },
  statusPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.4),
    fontSize: 12,
    fontWeight: 700,
    borderRadius: 999,
    padding: theme.spacing(0.4, 1),
    width: 'fit-content',
    minWidth: 98,
    justifySelf: 'start',
  },
  statusDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    flexShrink: 0,
  },
  statusVerified: {
    color: theme.palette.success.dark,
    backgroundColor: alpha(theme.palette.success.main, 0.15),
  },
  statusVerifiedDot: {
    backgroundColor: theme.palette.success.main,
  },
  statusNeedsInput: {
    color: theme.palette.error.dark,
    backgroundColor: alpha(theme.palette.error.main, 0.15),
  },
  statusNeedsInputDot: {
    backgroundColor: theme.palette.error.main,
  },
  statusInProgress: {
    color: theme.palette.warning.dark,
    backgroundColor: alpha(theme.palette.warning.main, 0.15),
  },
  statusInProgressDot: {
    backgroundColor: theme.palette.warning.main,
  },
  statusNeutral: {
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.grey[100],
  },
  statusNeutralDot: {
    backgroundColor: theme.palette.grey[500],
  },
  expandChevron: {
    color: theme.palette.text.secondary,
    fontSize: 18,
    justifySelf: 'end',
  },
  // ── Entities list ──────────────────────────────────────────────────────
  entityRowContainer: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  entityRowNeedsInput: {
    backgroundColor: alpha(theme.palette.error.main, 0.05),
  },
  entityGridRow: {
    display: 'grid',
    gridTemplateColumns: '2fr 1.4fr 0.8fr 1.2fr 1fr 32px 32px',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1.75, 2),
    cursor: 'pointer',
  },
  entityExpandContent: {
    padding: theme.spacing(0, 2, 2.5),
  },
  entityDetailRow: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    rowGap: theme.spacing(2),
    columnGap: theme.spacing(3),
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    borderBottom: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
  },
  entityDetailLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.3,
    textTransform: 'uppercase',
    color: MUTED_INFO_TEXT,
    marginBottom: theme.spacing(0.25),
  },
  entityDetailValue: {
    fontSize: 14,
  },
  propertyDetailGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    rowGap: theme.spacing(2),
    columnGap: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  propertyDetailRow: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    rowGap: theme.spacing(2),
    columnGap: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  entityAttentionTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: theme.palette.error.dark,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(0.5),
  },
  entityAttentionText: {
    fontSize: 14,
    color: theme.palette.text.primary,
  },
  entityDocsTitle: {
    fontSize: 14,
    fontWeight: 700,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(0.5),
  },
  entityDocsText: {
    fontSize: 14,
    color: 'var(--info-600)',
  },
  entityActionsRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: theme.spacing(2.5),
  },
  entityNoDocumentLink: {
    fontSize: 14,
    color: theme.palette.text.primary,
    cursor: 'pointer',
  },
  uploadSupportingDocButton: {
    border: '1px solid var(--info-600)',
    borderRadius: 999,
    backgroundColor: 'transparent',
    color: 'var(--info-600)',
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: 0.3,
    textTransform: 'uppercase',
    padding: theme.spacing(1, 2.5),
    cursor: 'pointer',
  },
  licenseGridRow: {
    display: 'grid',
    gridTemplateColumns: '1.6fr 1.2fr 0.8fr 1fr 1.6fr 1.2fr',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1.75, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  // ── In progress list (Overview) ────────────────────────────────────────
  inProgressGridRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 0.9fr) minmax(0, 1.2fr) minmax(0, 0.8fr) minmax(0, 0.8fr) 32px',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1.75, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  inProgressGridRowError: {
    backgroundColor: alpha(theme.palette.error.main, 0.04),
  },
  agentRunsErrorText: {
    fontSize: 12,
    color: theme.palette.error.dark,
  },
  agentRunsEmpty: {
    padding: theme.spacing(4, 2),
    textAlign: 'center',
    fontSize: 13,
    color: MUTED_INFO_TEXT,
  },
  // ── Agent runs status tabs ─────────────────────────────────────────────
  agentRunsTabs: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  agentRunsTab: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    height: 36,
    padding: theme.spacing(0, 1.5),
    borderRadius: 999,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    fontSize: 13,
    fontWeight: 700,
    fontFamily: 'inherit',
    cursor: 'pointer',
  },
  agentRunsTabActive: {
    borderColor: RC_INK,
    backgroundColor: RC_INK,
    color: theme.palette.common.white,
  },
  agentRunsTabCount: {
    minWidth: 20,
    height: 20,
    padding: theme.spacing(0, 0.75),
    boxSizing: 'border-box',
    borderRadius: 999,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 11,
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.text.secondary,
  },
  agentRunsTabCountActive: {
    backgroundColor: alpha(theme.palette.common.white, 0.2),
    color: theme.palette.common.white,
  },
  roleChip: {
    display: 'inline-block',
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
    fontSize: 12,
    fontWeight: 700,
    borderRadius: 999,
    padding: theme.spacing(0.4, 1.25),
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.text.primary,
  },
  viewIconButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.text.secondary,
    cursor: 'pointer',
  },
}));
